<template>
  <VueQueryDevtools v-if="!devtoolsDisabled" />
  <BaseAppearanceRoot data-test-id="app">
    <template v-if="!isLoading">
      <RouterView />
      <CookieConsent v-if="locationStore.setupData?.showConsentBanner" />
    </template>
    <template v-else>
      <UISkeletonSPA />
    </template>
  </BaseAppearanceRoot>
  <AuthWatchdog />
  <Notifications width="300px" position="bottom center" :speed="250" />
  <MockedRequestsDisplay :devtools-z-index="devtoolsZIndex" />
</template>

<script async lang="ts" setup>
import { useBookingPortalCustomDomainRedirect } from '@booking-portal/composables/use-custom-domain-redirect';
import { useCustomJavascript } from '@booking-portal/composables/use-custom-javascript';
import { useI18nLocale } from '@booking-portal/composables/use-i18n-locale';
import { usePageTitle } from '@booking-portal/composables/use-page-title';
import { useRemoteTranslations } from '@booking-portal/composables/use-remote-translations';
import { useSetupData } from '@booking-portal/composables/use-setup-data';
import { useVeeValidate } from '@booking-portal/composables/use-vee-validate';
import { devtoolsZIndex } from '@booking-portal/config/constants';
import AuthWatchdog from '@booking-portal/modules/auth/components/AuthWatchdog.vue';
import BaseAppearanceRoot from '@booking-portal/modules/base/components/BaseAppearanceRoot.vue';
import CookieConsent from '@booking-portal/modules/cookie/components/CookieConsent.vue';
import UISkeletonSPA from '@booking-portal/modules/ui/skeletons/components/UISkeletonSPA.vue';
import { useAppStore } from '@booking-portal/stores/app-store';
import { useAuthStore } from '@booking-portal/stores/auth-store';
import { useConsentStore } from '@booking-portal/stores/consent-store';
import { isExternalRedirectInProgress } from '@booking-portal/utils/external-redirect';
import { Notifications } from '@kyvg/vue3-notification';
import { VueQueryDevtools } from '@tanstack/vue-query-devtools';
import { MockedRequestsDisplay } from 'shared/msw';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

const devtoolsDisabled = import.meta.env.VITE_DISABLE_DEVTOOLS === 'true';

useAuthStore();
useSetupData();
// Initialize consent store to ensure GA/GTM tracking setup runs
// even when the built-in cookie banner is disabled (e.g., when using an external CMP like CCM19)
useConsentStore();
useCustomJavascript();
usePageTitle();
useVeeValidate();
useI18nLocale();

const locationStore = useAppStore();

// Loads the translations from the remote server
const { isLoading: isLoadingTranslations } = useRemoteTranslations();

// redirects the user to the correct custom domain if it exists. This should happen for both apps
const { isLoading: isLoadingRedirect } = useBookingPortalCustomDomainRedirect();

const isLoading = computed(
  () => isLoadingTranslations.value || isLoadingRedirect.value
);

const router = useRouter();

router.onError((error, to) => {
  // A cross-origin redirect (window.location.href = otherOrigin) cancels
  // in-flight chunk fetches in Safari, which surfaces here as a module
  // import error. Reloading via to.fullPath would resolve to the current
  // (old) origin and override the cross-origin navigation.
  if (isExternalRedirectInProgress.value) {
    return;
  }

  const errors = [
    'Failed to fetch dynamically imported module',
    'Importing a module script failed',
  ];
  if (errors.some((e) => error.message.includes(e))) {
    window.location.href = to.fullPath;
  }
});
</script>

<style>
.jgpts .jgptc_button-container {
  bottom: 40px !important;
}

.jgpts .jgptc_container.jgptc_open .jgptc_iframe-container {
  bottom: 100px !important;
}
</style>
